/**
 * Notches Stock Swing Signals page.
 *
 * Lives at /notches/dashboard/signals/stocks.
 * Reads from GET /notches/stocks/signals (prod-notches-stock-signals DDB).
 *
 * Displays full trade plans: entry / target / stop / R:R / hold days,
 * rationale bullets, and preset-ranked score badge.
 *
 * Design doc: docs/wiki/decisions/2026-05-26-stock-scorer-final.md
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Navigation } from '../Navigation';
import { hasSubscription } from '../../lib/entitlements';
import { ENTITLEMENTS } from '../../constants/entitlements';
import { MacroStressPanel } from '../MacroStressPanel';
import { IntradayChart } from '../IntradayChart';
import { IntradayCandle, MacroState } from '../../services/stockSignalsService';
import { marketRegimeService } from '../../services/marketRegimeService';

// ---- Color palette (matches Notches Options palette) -------------------------
const c = {
  bg: '#0f172a',
  card: '#1f2937',
  cardAlt: '#1a2332',
  border: '#374151',
  text: '#f9fafb',
  muted: '#9ca3af',
  dim: '#6b7280',
  green: '#16a34a',
  greenBg: '#16a34a20',
  blue: '#3b82f6',
  blueBg: '#3b82f620',
  amber: '#d97706',
  amberBg: '#d9770620',
  red: '#dc2626',
  redBg: '#dc262620',
};

// ---- Types -------------------------------------------------------------------

interface StockTechnicals {
  rsi_14?: number | null;
  sma_20?: number | null;
  sma_50?: number | null;
  current_close?: number | null;
  volume_ratio?: number | null;
  trend_direction?: string | null;
  trend_strength?: number | null;
  momentum_20d?: number | null;
  bb_position?: number | null;
  bb_width?: number | null;
  macd_histogram?: number | null;
  atr_14?: number | null;
  high_20?: number | null;
  low_20?: number | null;
  avwap_swing_distance?: number | null;
}

interface ScoreBreakdown {
  technical?: number | null;
  volume?: number | null;
  ivr?: number | null;
  trend?: number | null;
}

interface StockSignal {
  signal_id: string;
  symbol: string;
  direction: string;
  strategy_id: string | null;
  strategy_name: string | null;
  rationale: string[];
  // Trade plan
  entry_price: number | null;
  target_price: number | null;
  stop_loss: number | null;
  trailing_stop: number | null;
  current_price: number | null;
  high_since_entry: number | null;
  risk_reward_ratio: number | null;
  expected_hold_days: number | null;
  volatility_pct: number | null;
  // Scores
  score: number | null;
  scores_by_preset: Record<string, number> | null;
  score_breakdown: ScoreBreakdown;
  weights: Record<string, number>;
  // Lifecycle
  status: string | null;
  opened_at: string | null;
  last_updated: string | null;
  timestamp: string;
  snapshot_hour: string | null;
  date: string;
  technicals: StockTechnicals;
  // Intraday chart data (loaded by API from prod-price-history-intraday)
  intraday_candles?: IntradayCandle[];
}

interface StockSignalsResponse {
  date: string;
  preset: string;
  signals: StockSignal[];
  total: number;
  macro?: MacroState | null;
  message?: string;
}

// ---- Helpers -----------------------------------------------------------------

const API_BASE =
  process.env.REACT_APP_API_URL ||
  'https://4jbhzumjk7.execute-api.us-east-2.amazonaws.com/prod';

const fmt = (n: any, d = 1): string =>
  n == null ? '--' : Number(n).toFixed(d);

const pct = (num: number | null, denom: number | null, d = 1): string => {
  if (num == null || denom == null || denom === 0) return '--';
  return ((num / denom) * 100).toFixed(d) + '%';
};

const scoreColor = (score: number | null): string => {
  if (score == null) return c.muted;
  if (score >= 70) return c.green;
  if (score >= 50) return c.amber;
  return c.red;
};

const scoreBg = (score: number | null): string => {
  if (score == null) return 'transparent';
  if (score >= 70) return c.greenBg;
  if (score >= 50) return c.amberBg;
  return c.redBg;
};

const directionBadge = (dir: string | null | undefined) => {
  if (!dir) return null;
  const isLong = dir === 'long';
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: 10,
      fontSize: 11,
      fontWeight: 700,
      background: isLong ? c.greenBg : c.redBg,
      color: isLong ? c.green : c.red,
      textTransform: 'uppercase' as const,
      letterSpacing: '0.04em',
    }}>
      {isLong ? 'BUY LONG' : 'SELL SHORT'}
    </span>
  );
};

const volLabel = (volPct: number | null): string => {
  if (volPct == null) return '--';
  if (volPct < 2) return 'low';
  if (volPct < 4) return 'normal';
  return 'high';
};

const volColor = (volPct: number | null): string => {
  if (volPct == null) return c.muted;
  if (volPct < 2) return c.blue;
  if (volPct < 4) return c.green;
  return c.amber;
};

const PRESETS = ['conservative', 'moderate', 'aggressive'] as const;
type Preset = typeof PRESETS[number];

// ---- Signal Card -------------------------------------------------------------

interface SignalCardProps {
  sig: StockSignal;
  preset: Preset;
  isOpen: boolean;
  onToggle: () => void;
}

const SignalCard: React.FC<SignalCardProps> = ({ sig, preset, isOpen, onToggle }) => {
  const displayScore = sig.scores_by_preset
    ? (sig.scores_by_preset[preset] ?? sig.score)
    : sig.score;
  const scoreInt = displayScore != null ? Math.round(displayScore) : null;

  const entry = sig.entry_price;
  const target = sig.target_price;
  const stop = sig.stop_loss ?? sig.trailing_stop;
  const rr = sig.risk_reward_ratio;
  const holdDays = sig.expected_hold_days;
  const tech = sig.technicals || {};

  // Gain to target / loss to stop (relative to entry)
  const targetGainPct = entry && target ? ((target - entry) / entry * 100) : null;
  const stopLossPct   = entry && stop   ? ((stop   - entry) / entry * 100) : null;

  return (
    <div style={{
      background: c.card,
      border: `1px solid ${c.border}`,
      borderLeft: `3px solid ${scoreColor(scoreInt)}`,
      borderRadius: 6,
      marginBottom: 10,
      overflow: 'hidden',
    }}>
      {/* ---- Card header row ---- */}
      <div
        onClick={onToggle}
        style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '10px 16px', cursor: 'pointer', flexWrap: 'wrap',
        }}
      >
        {/* Score badge */}
        <div style={{
          minWidth: 50, textAlign: 'center',
          background: scoreBg(scoreInt), color: scoreColor(scoreInt),
          borderRadius: 6, padding: '4px 8px',
          fontSize: 20, fontWeight: 800, lineHeight: 1.1,
          flexShrink: 0,
        }}>
          {scoreInt != null ? scoreInt : '--'}
          <div style={{ fontSize: 10, fontWeight: 400, color: scoreColor(scoreInt) }}>/ 100</div>
        </div>

        {/* Symbol + strategy */}
        <div style={{ minWidth: 80 }}>
          <div style={{ fontWeight: 800, fontSize: 17, color: c.text }}>{sig.symbol}</div>
          <div style={{ fontSize: 11, color: c.muted, marginTop: 1 }}>
            {sig.strategy_name || 'Swing Signal'}
          </div>
        </div>

        {/* Direction badge */}
        <div style={{ flexShrink: 0 }}>
          {directionBadge(sig.direction)}
        </div>

        {/* Trade plan key numbers */}
        <div style={{ display: 'flex', gap: 14, fontSize: 12, flexWrap: 'wrap', flex: 1 }}>
          <div>
            <div style={{ color: c.dim, fontSize: 10 }}>Entry</div>
            <div style={{ color: c.text, fontWeight: 600 }}>${fmt(entry, 2)}</div>
          </div>
          <div>
            <div style={{ color: c.dim, fontSize: 10 }}>Target</div>
            <div style={{ color: c.green, fontWeight: 600 }}>
              ${fmt(target, 2)}
              {targetGainPct != null && (
                <span style={{ color: c.green, fontSize: 10, marginLeft: 3 }}>
                  (+{targetGainPct.toFixed(1)}%)
                </span>
              )}
            </div>
          </div>
          <div>
            <div style={{ color: c.dim, fontSize: 10 }}>Stop</div>
            <div style={{ color: c.red, fontWeight: 600 }}>
              ${fmt(stop, 2)}
              {stopLossPct != null && (
                <span style={{ color: c.red, fontSize: 10, marginLeft: 3 }}>
                  ({stopLossPct.toFixed(1)}%)
                </span>
              )}
            </div>
          </div>
          <div>
            <div style={{ color: c.dim, fontSize: 10 }}>R:R</div>
            <div style={{ color: c.text, fontWeight: 600 }}>
              {rr != null ? `1:${rr.toFixed(1)}` : '--'}
            </div>
          </div>
          <div>
            <div style={{ color: c.dim, fontSize: 10 }}>Hold</div>
            <div style={{ color: c.text, fontWeight: 600 }}>
              {holdDays != null ? `${holdDays}d` : '--'}
            </div>
          </div>
          <div>
            <div style={{ color: c.dim, fontSize: 10 }}>Vol</div>
            <div style={{ color: volColor(sig.volatility_pct), fontWeight: 600 }}>
              {volLabel(sig.volatility_pct)}
            </div>
          </div>
        </div>

        {/* Expand toggle */}
        <div style={{ fontSize: 11, color: c.dim, marginLeft: 'auto', flexShrink: 0 }}>
          {isOpen ? 'collapse' : 'details'}
        </div>
      </div>

      {/* ---- Expanded detail ---- */}
      {isOpen && (
        <div style={{ borderTop: `1px solid ${c.border}` }}>

          {/* Intraday price chart (only shown when expanded to save vertical space) */}
          {sig.intraday_candles && sig.intraday_candles.length > 0 && (
            <IntradayChart
              candles={sig.intraday_candles}
              entryPrice={Number(sig.entry_price)}
              targetPrice={Number(sig.target_price)}
              stopLoss={Number(sig.trailing_stop ?? sig.stop_loss)}
              symbol={sig.symbol}
            />
          )}

          {/* Rationale bullets */}
          {sig.rationale && sig.rationale.length > 0 && (
            <div style={{ padding: '12px 16px', borderBottom: `1px solid ${c.border}` }}>
              <div style={{ color: c.muted, fontWeight: 600, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
                Strategy rationale
              </div>
              <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
                {sig.rationale.map((r, i) => (
                  <li key={i} style={{ fontSize: 12, color: c.muted, marginBottom: 4, lineHeight: 1.5 }}>
                    {r}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Trade plan details + scores side by side */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: 0,
            fontSize: 12,
          }}>
            {/* Trade plan detail */}
            <div style={{ padding: '12px 16px', borderRight: `1px solid ${c.border}` }}>
              <div style={{ color: c.muted, fontWeight: 600, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
                Trade plan
              </div>
              {[
                ['Entry', `$${fmt(entry, 2)}`],
                ['Target', `$${fmt(target, 2)}  (+${targetGainPct != null ? targetGainPct.toFixed(1) + '%' : '--'})`],
                ['Stop Loss', `$${fmt(stop, 2)}  (${stopLossPct != null ? stopLossPct.toFixed(1) + '%' : '--'})`],
                ['Risk:Reward', rr != null ? `1:${rr.toFixed(2)}` : '--'],
                ['Hold (est)', holdDays != null ? `${holdDays} days` : '--'],
                ['ATR volatility', `${fmt(sig.volatility_pct, 1)}% (${volLabel(sig.volatility_pct)})`],
              ].map(([label, val]) => (
                <div key={label as string} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span style={{ color: c.dim }}>{label}</span>
                  <span style={{ color: c.text }}>{val}</span>
                </div>
              ))}
            </div>

            {/* Score breakdown */}
            <div style={{ padding: '12px 16px', borderRight: `1px solid ${c.border}` }}>
              <div style={{ color: c.muted, fontWeight: 600, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
                Score breakdown ({preset})
              </div>
              {(['technical', 'volume', 'ivr', 'trend'] as const).map((comp) => {
                const raw = (sig.score_breakdown as any)[comp];
                const w = (sig.weights as any)[comp] ?? 0;
                const val = raw != null ? Number(raw) : null;
                return (
                  <div key={comp} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ color: c.dim, textTransform: 'capitalize' }}>{comp}</span>
                    <span>
                      <span style={{ color: scoreColor(val) }}>{val != null ? val.toFixed(0) : '--'}</span>
                      <span style={{ color: c.dim }}> x{(w * 100).toFixed(0)}%</span>
                    </span>
                  </div>
                );
              })}
              <div style={{
                marginTop: 8, paddingTop: 6, borderTop: `1px solid ${c.border}`,
                display: 'flex', justifyContent: 'space-between', fontWeight: 700,
              }}>
                <span style={{ color: c.muted }}>Score</span>
                <span style={{ color: scoreColor(scoreInt) }}>
                  {displayScore != null ? displayScore.toFixed(1) : '--'}
                </span>
              </div>
              {/* Preset comparison */}
              {sig.scores_by_preset && (
                <div style={{ marginTop: 10 }}>
                  <div style={{ color: c.dim, fontSize: 10, marginBottom: 4 }}>All presets:</div>
                  {PRESETS.map((p) => {
                    const ps = sig.scores_by_preset![p];
                    return ps != null ? (
                      <div key={p} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                        <span style={{ color: p === preset ? c.text : c.dim, textTransform: 'capitalize', fontSize: 11 }}>{p}</span>
                        <span style={{ color: scoreColor(ps), fontSize: 11 }}>{Math.round(ps)}</span>
                      </div>
                    ) : null;
                  })}
                </div>
              )}
            </div>

            {/* Technicals strip */}
            <div style={{ padding: '12px 16px' }}>
              <div style={{ color: c.muted, fontWeight: 600, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
                Technicals
              </div>
              {[
                ['Price', `$${fmt(tech.current_close, 2)}`],
                ['RSI 14', fmt(tech.rsi_14, 1)],
                ['Vol ratio', `${fmt(tech.volume_ratio, 2)}x`],
                ['SMA 20 / 50', `$${fmt(tech.sma_20, 1)} / $${fmt(tech.sma_50, 1)}`],
                ['MACD hist', tech.macd_histogram != null ? (Number(tech.macd_histogram) >= 0 ? '+' : '') + fmt(tech.macd_histogram, 3) : '--'],
                ['BB position', tech.bb_position != null ? `${(Number(tech.bb_position) * 100).toFixed(0)}%` : '--'],
                ['AVWAP dist', tech.avwap_swing_distance != null ? (Number(tech.avwap_swing_distance) >= 0 ? '+' : '') + fmt(tech.avwap_swing_distance, 1) + '%' : '--'],
              ].map(([label, val]) => (
                <div key={label as string} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span style={{ color: c.dim }}>{label}</span>
                  <span style={{ color: c.text }}>{val}</span>
                </div>
              ))}
              <div style={{ marginTop: 10, fontSize: 10, color: c.dim, wordBreak: 'break-all' }}>
                {sig.signal_id}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ---- Main component ----------------------------------------------------------

export const StockSignalsPreview: React.FC = () => {
  const { user, token } = useAuth();

  const hasPreview = hasSubscription(user, ENTITLEMENTS.NOTCHES_PREVIEW);
  const hasStocks  = hasSubscription(user, ENTITLEMENTS.SIGNALS_STOCKS);

  const [preset, setPreset] = useState<Preset>(() => {
    const up = (user as any)?.notchesPreset as string | undefined;
    if (up && PRESETS.includes(up as Preset)) return up as Preset;
    return 'moderate';
  });

  const [data, setData]       = useState<StockSignalsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [macro, setMacro]     = useState<MacroState | null>(null);

  const fetchSignals = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await axios.get<StockSignalsResponse>(
        `${API_BASE}/notches/stocks/signals`,
        {
          params: { preset },
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setData(resp.data);
      // Macro comes EXCLUSIVELY from /market/regime's equity_macro since
      // 2026-06-27 -- single source = no flash, equity-specific (VIX + SPY)
      // is the right lens for stocks. The bundled `resp.data.macro` is
      // intentionally ignored (it is 4-factor cross-asset data, wrong for
      // an equity product, and merging two sources produced the
      // orange-to-red reload flash).
      marketRegimeService.get(token).then((r) => {
        setMacro((r.equity_macro as any) || null);
      }).catch(() => {});
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to load signals';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [token, preset]);

  useEffect(() => {
    if (hasPreview || hasStocks) fetchSignals();
  }, [fetchSignals, hasPreview, hasStocks]);

  if (!hasPreview && !hasStocks) {
    return <Navigate to="/notches" replace />;
  }

  const toggleExpanded = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // ---- Render ----------------------------------------------------------------
  return (
    <div style={{ background: c.bg, minHeight: '100vh', color: c.text }}>
      <Navigation />
      <div style={{ padding: '24px 32px', maxWidth: 1100, margin: '0 auto' }}>

        {/* Breadcrumb */}
        <div style={{ fontSize: 12, marginBottom: 10, color: c.muted }}>
          <Link to="/notches/dashboard" style={{ color: c.muted, textDecoration: 'none' }}>Notches</Link>
          <span style={{ color: c.dim, margin: '0 6px' }}>/</span>
          <Link to="/notches/dashboard/signals" style={{ color: c.muted, textDecoration: 'none' }}>Research</Link>
          <span style={{ color: c.dim, margin: '0 6px' }}>/</span>
          <span style={{ color: c.text }}>Stocks</span>
        </div>

        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
          marginBottom: 8, paddingBottom: 10, borderBottom: `2px solid ${c.green}`,
          flexWrap: 'wrap', gap: 8,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ width: 8, height: 28, borderRadius: 2, background: c.green, flexShrink: 0 }} />
            <div>
              <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700 }}>
                Stock Swing Research
              </h1>
              <p style={{ margin: '4px 0 0', fontSize: 12, color: c.muted }}>
                Entry / target / stop levels from 3 long swing strategies.
                Scored by 4-component model (technical + volume + IVR + trend).
              </p>
            </div>
          </div>
          <a
            href="/equity-signals"
            style={{
              fontSize: 12, color: c.muted, textDecoration: 'none',
              border: `1px solid ${c.border}`, padding: '4px 10px', borderRadius: 4,
              whiteSpace: 'nowrap',
            }}
          >View legacy research</a>
        </div>

        {/* Macro stress banner */}
        {macro && <MacroStressPanel macro={macro} penaltyTarget="long" />}

        {/* Preset toggle */}
        <div style={{ display: 'flex', gap: 8, margin: '16px 0', alignItems: 'center' }}>
          <span style={{ fontSize: 11, color: c.dim, marginRight: 4 }}>Preset:</span>
          {PRESETS.map((p) => (
            <button
              key={p}
              onClick={() => setPreset(p)}
              style={{
                padding: '5px 14px', borderRadius: 4, fontSize: 12, fontWeight: 600,
                border: `1px solid ${preset === p ? c.green : c.border}`,
                background: preset === p ? c.greenBg : 'transparent',
                color: preset === p ? c.green : c.muted,
                cursor: 'pointer',
                textTransform: 'capitalize' as const,
              }}
            >{p}</button>
          ))}
        </div>

        {/* Body */}
        {loading && (
          <div style={{ color: c.muted, padding: '32px 0', textAlign: 'center', fontSize: 14 }}>
            Loading research...
          </div>
        )}

        {!loading && error && (
          <div style={{
            background: c.redBg, border: `1px solid ${c.red}`, borderRadius: 6,
            padding: '12px 16px', color: c.red, fontSize: 13, marginTop: 16,
          }}>
            {error}
          </div>
        )}

        {!loading && !error && data && data.total === 0 && (
          <div style={{
            background: c.card, border: `1px dashed ${c.border}`, borderRadius: 8,
            padding: '20px 24px', color: c.muted, fontSize: 13, marginTop: 16,
          }}>
            {data.message || `No picks found for ${data.date}. Generator runs weekdays after 6 PM ET.`}
          </div>
        )}

        {!loading && !error && data && data.total > 0 && (
          <>
            <div style={{ fontSize: 12, color: c.muted, marginBottom: 14 }}>
              {data.total} pick{data.total !== 1 ? 's' : ''} for {data.date}
              {' -- '}sorted by {preset} score
            </div>

            {/* Signal cards */}
            {data.signals.map((sig) => (
              <SignalCard
                key={sig.signal_id}
                sig={sig}
                preset={preset}
                isOpen={expanded.has(sig.signal_id)}
                onToggle={() => toggleExpanded(sig.signal_id)}
              />
            ))}

            {/* Footer note */}
            <div style={{
              marginTop: 20, padding: '10px 14px',
              background: c.cardAlt, border: `1px solid ${c.border}`,
              borderRadius: 6, fontSize: 11, color: c.dim,
            }}>
              Shadow window: running in parallel with legacy /equity-signals.
              All picks are long-only (4-year backtest decision).
              Generator fires weekdays at 10:30 PM UTC after market data settles.
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default StockSignalsPreview;
